import React, { useMemo, useCallback, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Typography, Loader } from '../../common';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from 'redux/store';
import { useResponsiveHeightPreset } from '../../../hooks/useResponsiveHeight';
import { useDrop, useDrag } from 'react-dnd';
import {
    AvailableIcon,
    CallerIcon,
    DurationIcon,
    ExtensionIcon,
    DNDIcon,
    LoggedOutIcon,
    OnCallIcon,
    UserListIcon,
    WaitingIcon,
} from '../../../assets';
import ExtensionTimer from './ExtensionTimer';
import { CrashSafeWrapper } from '../../CrashSafeWrapper';

import { UsersDevicesList } from '../../common';
import { ExtensionStatus } from '../../../redux/slices/apps.slice';
import { Utils } from '../../../utils/Utils';

type StatusKey = ExtensionStatus;

const STATUS_ICONS: Record<StatusKey, string> = {
    [ExtensionStatus.Ringing]: OnCallIcon,
    [ExtensionStatus.InCall]: CallerIcon,
    [ExtensionStatus.Available]: AvailableIcon,
    [ExtensionStatus.Offline]: LoggedOutIcon,
};

const STATUS_PRIORITY: Record<StatusKey, number> = {
    [ExtensionStatus.InCall]: 0,
    [ExtensionStatus.Ringing]: 1,
    [ExtensionStatus.Available]: 2,
    [ExtensionStatus.Offline]: 4,
};

const STATIC_ICONS = {
    user: UserListIcon,
    ext: ExtensionIcon,
    caller: CallerIcon,
    duration: DurationIcon,
};

const ICON_COMBINATIONS = {
    [ExtensionStatus.Ringing]: { ...STATIC_ICONS, status: STATUS_ICONS[ExtensionStatus.Ringing] },
    [ExtensionStatus.InCall]: { ...STATIC_ICONS, status: STATUS_ICONS[ExtensionStatus.InCall] },
    [ExtensionStatus.Available]: { ...STATIC_ICONS, status: STATUS_ICONS[ExtensionStatus.Available] },
    [ExtensionStatus.Offline]: { ...STATIC_ICONS, status: STATUS_ICONS[ExtensionStatus.Offline] },
    'Default': { ...STATIC_ICONS, status: AvailableIcon },
};

const getIconsForStatus = (status: StatusKey) => ICON_COMBINATIONS[status] || ICON_COMBINATIONS['Default'];

const preloadImages = () => {
    const allIcons = [
        ...Object.values(STATIC_ICONS),
        ...Object.values(STATUS_ICONS),
        AvailableIcon,
        DNDIcon
    ];

    allIcons.forEach(iconSrc => {
        if (!document.querySelector(`link[href="${iconSrc}"]`)) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = iconSrc;
            link.crossOrigin = 'anonymous';
            document.head.appendChild(link);
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = iconSrc;
    });
};

let preloadInitialized = false;
if (typeof window !== 'undefined' && !preloadInitialized) {
    preloadImages();
    preloadInitialized = true;
}

const LAYOUT_CONFIG = {
    headerHeight: 60,
    searchBarHeight: 60,
    padding: 40,
    minHeight: 300,
    itemHeight: 80,
};

const ICON_CACHE_STYLES = `
    .extension-image, .extension-list-item img {
        image-rendering: auto;
        will-change: auto;
        width: 5rem;
    }
    
    body::after {
        content: "";
        position: absolute;
        left: -9999px;
        background-image: 
            url("${UserListIcon}"),
            url("${ExtensionIcon}"),
            url("${CallerIcon}"),
            url("${DurationIcon}"),
            url("${WaitingIcon}"),
            url("${OnCallIcon}"),
            url("${AvailableIcon}"),
            url("${LoggedOutIcon}"),
            url("${DNDIcon}");
    }
`;

interface ExtensionsListProps {
    loading?: boolean;
    extensionSearch?: string;
    sortOrder?: 'asc' | 'desc';
    sortType?: 'name' | 'extension' | 'status';
    excludeExts?: string[];
    renderItem?: React.FC<IExtensionListItemProps>
    itemHeight?: number;
    filters?: any;
    onDropCallToExtension?: (callUUID: string, targetExt: string, callees: any[], callerIDNumber: string, callerIDName: string, destination: string) => void;
}

export interface IExtensionListItemProps {
    index: number;
    style: React.CSSProperties;
    data: {
        extensions: any[];
        onDropCallToExtension?: (callUUID: string, targetExt: string, callees: any[], callerIDNumber: string, callerIDName: string, destination: string) => void;
    };
}

const ExtensionItem: React.FC<IExtensionListItemProps> = React.memo(({ index, style, data }) => {
    const { extensions, onDropCallToExtension } = data;
    const extension = extensions[index];

    if (!extension) return null;

    const isOnCall = extension.status === ExtensionStatus.InCall;

    const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
        accept: 'CALL',
        drop: (item: any) => {
            const callUUID = item?.callUUID;
            const targetExt = extension.ext;
            if (callUUID && targetExt) {
                onDropCallToExtension?.(callUUID, targetExt, item.callees, item.callerIDNumber, item.callerIDName, item.destination);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }), [extension, onDropCallToExtension]);

    const [{ isDragging }, dragRef] = useDrag(() => ({
        type: 'EXT',
        item: { ext: extension.ext, callees: extension.callees, callerIDNumber: extension.callerIDNumber, callerIDName: extension.callerIDName },
        canDrag: () => Boolean(extension.ext),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }), [extension]);

    const dropActive = isOver && canDrop;

    return (
        <div
            ref={dropRef}
            style={
                dropActive
                    ? { ...style, outline: '2px dashed #22c55e', borderRadius: 8, background: 'rgba(34, 197, 94, 0.08)' }
                    : style
            }
        >
            <li
                ref={dragRef}
                className="user-list-item"
                style={{ borderBottom: '1px solid #e0e0e0', ...(isDragging ? { outline: '2px dashed #3b82f6', borderRadius: 8, cursor: 'grabbing' } : { cursor: 'grab' }) }}
            >
                <div className="image">
                    <img
                        src={extension.icons.user}
                        alt="User Icon"
                        className="extension-image"
                        loading="lazy"
                    />
                </div>
                <div className="user-name">
                    {isOnCall ? (
                        <div className="name-number">
                            <Typography variant="subHeading-1" className="extension-name">
                                {extension.name}
                            </Typography>
                            <Typography variant="small-text-3" className="extension-phone">
                                {extension.phone || 'Unknown'}
                            </Typography>
                        </div>
                    ) : (
                        <Typography variant="subHeading-1" className="extension-name">
                            {extension.name}
                        </Typography>
                    )}
                </div>
                <div className="call-info">
                    {isOnCall ? (
                        <div className="caller-name-duration-container">
                            <div className="caller-name">
                                <img
                                    src={extension.icons.caller}
                                    alt="Caller Icon"
                                    loading="lazy"
                                />
                                <Typography variant="small-text-3">{extension.callerName}</Typography>
                            </div>
                            <div className="duration">
                                <img
                                    src={extension.icons.duration}
                                    alt="Duration Icon"
                                    loading="lazy"
                                />
                                <ExtensionTimer
                                    startTime={extension.duration}
                                    variant="small-text-3"
                                    className="duration"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="ext-name">
                            <img
                                src={extension.icons.ext}
                                alt="Ext icon"
                                loading="lazy"
                            />
                            <Typography variant="small-text-3">{extension.ext}</Typography>
                        </div>
                    )}
                </div>
                <div>
                    <div className="status">
                        <img
                            src={extension.icons.status}
                            alt="Status Icon"
                            loading="lazy"
                            style={{ width: '18px', height: '18px' }}
                        />
                        {extension.dndEnabled && <img
                            src={DNDIcon}
                            alt="Status Icon"
                            loading="lazy"
                            style={{ width: '18px', height: '18px' }}
                        />}

                        <Typography variant="subHeading-3">{Utils.renderStatus(extension.status)}</Typography>
                    </div>
                    <UsersDevicesList devices={extension.extensionDevices || []} />
                </div>
            </li>
        </div>
    );
}, (prevProps, nextProps) => {
    const prevExt = prevProps.data.extensions[prevProps.index];
    const nextExt = nextProps.data.extensions[nextProps.index];

    if (!prevExt && !nextExt) return true;
    if (!prevExt || !nextExt) return false;

    const prevDevices = prevExt.extensionDevices || [];
    const nextDevices = nextExt.extensionDevices || [];
    const devicesChanged = prevDevices.length !== nextDevices.length ||
        prevDevices.some((device: any, idx: number) => {
            const nextDevice = nextDevices[idx];
            return !nextDevice ||
                device.networkIp !== nextDevice.networkIp ||
                device.networkPort !== nextDevice.networkPort ||
                device.userAgent !== nextDevice.userAgent;
        });

    return (
        prevExt.id === nextExt.id &&
        prevExt.name === nextExt.name &&
        prevExt.status === nextExt.status &&
        prevExt.phone === nextExt.phone &&
        prevExt.callerName === nextExt.callerName &&
        prevExt.dndEnabled === nextExt.dndEnabled &&
        prevProps.style === nextProps.style &&
        !devicesChanged
    );
});

const ExtensionsList: React.FC<ExtensionsListProps> = ({
    loading,
    extensionSearch = '',
    sortOrder,
    sortType = 'extension',
    excludeExts,
    renderItem,
    itemHeight,
    filters,
    onDropCallToExtension
}) => {
    const { height: containerHeight } = useResponsiveHeightPreset('extensionList');

    const calls = useAppSelector((state: RootState) => state?.liveCalls?.calls);
    const extensions = useAppSelector((state: RootState) => state?.apps.data.extension);
    const extensionsLoader = useAppSelector((state: RootState) => state?.apps.loading);

    const prevExtensionsRef = useRef<any[]>([]);
    const stableExtensionsRef = useRef<any[]>([]);

    const stableExtensions = useMemo(() => {
        if (!Array.isArray(extensions)) return extensions;

        const hasChanged = extensions.length !== prevExtensionsRef.current.length ||
            extensions.some((ext, index) => {
                const prev = prevExtensionsRef.current[index];
                if (!prev) return true;
                
                const prevDevices = prev?.extensionDevices || [];
                const currDevices = ext?.extensionDevices || [];
                const devicesChanged = prevDevices.length !== currDevices.length ||
                    currDevices.some((device: any, idx: number) => {
                        const prevDevice = prevDevices[idx];
                        return !prevDevice ||
                            device.networkIp !== prevDevice.networkIp ||
                            device.networkPort !== prevDevice.networkPort ||
                            device.userAgent !== prevDevice.userAgent;
                    });
                
                return ext?.id !== prev?.id ||
                    ext?.name !== prev?.name ||
                    ext?.extension !== prev?.extension ||
                    ext?.status !== prev?.status ||
                    ext?.dndEnabled !== prev?.dndEnabled ||
                    ext?.forwardBusyEnabled !== prev?.forwardBusyEnabled ||
                    ext?.forwardNoAnswerEnabled !== prev?.forwardNoAnswerEnabled ||
                    ext?.forwardImmediateEnabled !== prev?.forwardImmediateEnabled ||
                    devicesChanged;
            });

        if (hasChanged) {
            prevExtensionsRef.current = extensions;
            stableExtensionsRef.current = extensions.map(ext => ({
                id: ext?.id || '',
                name: ext?.name || '',
                extension: ext?.extension || '',
                status: ext?.status || 'Available',
                extensionDevices: ext?.extensionDevices || [],
                dndEnabled: ext?.dndEnabled,
                forwardBusyEnabled: ext?.forwardBusyEnabled,
                forwardNoAnswerEnabled: ext?.forwardNoAnswerEnabled,
                forwardImmediateEnabled: ext?.forwardImmediateEnabled
            }));
        }

        return stableExtensionsRef.current;
    }, [extensions]);

    const activeCallsMap = useMemo(() => {
        const newMap = new Map();

        calls?.forEach((call: any) => {
            call?.callees?.forEach((callee: any) => {
                if (callee?.answerState === 'answered' && callee?.number) {
                    newMap.set(callee.number, {
                        callerIDNumber: call.callerIDNumber,
                        callerIDName: call.callerIDName,
                        startTime: call.startTime
                    });
                }
            });
        });

        return newMap;
    }, [calls]);

    const memoizedGetIconsForStatus = useCallback((status: StatusKey) => {
        return getIconsForStatus(status);
    }, []);

    const baseExtensions = useMemo(() => {
        if (!Array.isArray(stableExtensions)) {
            return [];
        }

        return stableExtensions.map((extension: any) => ({
            id: extension?.id || '',
            name: extension?.name?.trim() || extension?.extension || 'Unknown',
            ext: extension?.extension || '',
            status: (extension.status as StatusKey) || 'Available',
            searchString: `${extension?.name || ''} ${extension?.extension || ''} ${extension?.status || ''}`.toLowerCase(),
            extensionDevices: extension?.extensionDevices || [],
            dndEnabled: extension?.dndEnabled,
            forwardBusyEnabled: extension?.forwardBusyEnabled,
            forwardNoAnswerEnabled: extension?.forwardNoAnswerEnabled,
            forwardImmediateEnabled: extension?.forwardImmediateEnabled
        }));
    }, [stableExtensions]);

    const filteredExtensions = useMemo(() => {
        let result = !extensionSearch
            ? baseExtensions
            : baseExtensions.filter(extension =>
                extension.searchString.includes(extensionSearch.toLowerCase())
            );

        if (excludeExts?.length) {
            result = result.filter(extension => !excludeExts.includes(extension.ext));
        }

        if (filters?.status && Array.isArray(filters.status) && filters.status.length) {
            result = result.filter(extension => {
                if (filters.status.includes('DND') && extension.dndEnabled) {
                    return true;
                }
                
                if (filters.status.includes(ExtensionStatus.Available) && extension.dndEnabled) {
                    return false;
                }
                
                const hasNoDevices = !extension.extensionDevices || extension.extensionDevices.length === 0;
                const effectiveStatus = (extension.status && hasNoDevices) ? ExtensionStatus.Offline : extension.status;
                
                return filters.status.includes(effectiveStatus);
            });
        }

        return result;
    }, [baseExtensions, extensionSearch, excludeExts, filters]);

    const { filteredAndSortedExtensions } = useMemo(() => {
        const processedExtensions = filteredExtensions.map((extension) => {
            const activeCall = activeCallsMap.get(extension.ext);
            
            const hasNoDevices = !extension.extensionDevices || extension.extensionDevices.length === 0;
            const effectiveStatus = (extension.status && hasNoDevices) ? ExtensionStatus.Offline : extension.status;

            return {
                ...extension,
                status: effectiveStatus,
                phone: activeCall?.callerIDNumber || '',
                callerName: activeCall?.callerIDName || '',
                duration: activeCall?.startTime ?? '',
                icons: memoizedGetIconsForStatus(effectiveStatus),
            };
        });

        const sorted = [...processedExtensions].sort((a, b) => {
            const aPriority = STATUS_PRIORITY[a.status as StatusKey] ?? 4;
            const bPriority = STATUS_PRIORITY[b.status as StatusKey] ?? 4;

            if (sortType === 'status') {
                return aPriority - bPriority;
            }

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            if (sortType === 'name') {
                const aName = (a.name || '').toLowerCase();
                const bName = (b.name || '').toLowerCase();
                const nameComparison = aName.localeCompare(bName);
                return sortOrder === 'asc' ? nameComparison : -nameComparison;
            }

            const aExt = parseInt(a.ext) || 0;
            const bExt = parseInt(b.ext) || 0;
            const extComparison = aExt - bExt;
            if (!sortOrder) return extComparison;
            return sortOrder === 'asc' ? extComparison : -extComparison;
        });

        return { filteredAndSortedExtensions: sorted };
    }, [filteredExtensions, activeCallsMap, sortOrder, sortType]);

    const itemData = useMemo(() => ({
        extensions: filteredAndSortedExtensions,
        onDropCallToExtension,
    }), [filteredAndSortedExtensions, onDropCallToExtension]);

    if (extensionsLoader) {
        return (
            <div className="loader">
                <Loader />
            </div>
        );
    }

    if (!extensions || !extensions.length) {
        return (
            <Typography variant="text-1" className="text-center">
                No extensions found
            </Typography>
        );
    }

    const ItemRenderer = renderItem || ExtensionItem;

    return (
        <CrashSafeWrapper level="component" name="ExtensionsList">
            <>
                <style dangerouslySetInnerHTML={{ __html: ICON_CACHE_STYLES }} />
                <List
                    height={containerHeight}
                    width="100%"
                    itemCount={filteredAndSortedExtensions.length}
                    itemSize={itemHeight || LAYOUT_CONFIG.itemHeight}
                    itemData={itemData}
                    className="extension-list-container"
                    overscanCount={5}
                >
                    {ItemRenderer}
                </List>
            </>
        </CrashSafeWrapper>
    );
};

export default React.memo(ExtensionsList, (prevProps, nextProps) => {
    return (
        prevProps.loading === nextProps.loading &&
        prevProps.extensionSearch === nextProps.extensionSearch &&
        prevProps.sortOrder === nextProps.sortOrder &&
        prevProps.sortType === nextProps.sortType &&
        JSON.stringify(prevProps.filters) === JSON.stringify(nextProps.filters)
    );
});
